import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { tokens } from '../../styles/tokens'
import { OrderRowData } from './OrderTable'
import { InstrumentIcon } from '../common/InstrumentIcon'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import {
  ORDER_STATE_CANCELED,
  ORDER_STATE_CONFIRMED,
  ORDER_STATE_ERROR,
  ORDER_STATE_EXPIRED,
  ORDER_STATE_FILLED,
  ORDER_STATE_NEW,
  ORDER_STATE_PARTIAL_CANCELED,
  ORDER_STATE_PARTIAL_EXPIRED,
  ORDER_STATE_PARTIAL_FILLED,
  ORDER_STATE_REJECTED,
  ORDER_STATE_SENT_NEW,
} from '../../constants/codeLists'

import CheckIcon from '@mui/icons-material/CheckRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import { newThemeObject } from '../../newTheme'
import { formatNumber } from '../../utils/numbersFormatter'
import { ColoredIconButton } from '../common/ColoredIconButton'

import { useDispatch } from 'react-redux'
import { openDialog } from '../../redux/actions/dialogActions'
import EditIcon from '@mui/icons-material/EditRounded'
import { ORDER_DETAIL_DIALOG_ID } from '../../constants/dialogConstants'

// Karta pokynu pro mobil - nahrazuje sirokou tabulku (11 sloupcu), aby se nemuselo nic posouvat do stran.
const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${tokens.color.neutral[200]}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  rowColorSell: {
    backgroundColor: tokens.color.sellRow,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  cardHeaderText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flexGrow: 1,
  },
  cardName: {
    margin: 0,
    fontSize: '0.8rem',
    fontWeight: 600,
    color: tokens.color.neutral[900],
    overflowWrap: 'break-word',
  },
  cardIsin: {
    margin: 0,
    fontSize: '0.7rem',
    color: tokens.color.neutral[500],
  },
  cardOrderNumber: {
    margin: 0,
    fontSize: '0.7rem',
    color: tokens.color.neutral[500],
    whiteSpace: 'nowrap',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: theme.spacing(0.75, 1.5),
    marginTop: theme.spacing(1),
  },
  cardField: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  cardLabel: {
    margin: 0,
    fontSize: '0.65rem',
    color: tokens.color.neutral[500],
  },
  cardValue: {
    margin: '2px 0 0',
    fontSize: '0.75rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    overflowWrap: 'break-word',
  },
  rowOrderState: {
    borderRadius: '1rem',
    height: '2rem',
    alignContent: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 12px',
    display: 'inline-flex',
    alignItems: 'center',
  },
  rowOrderStateText: {
    opacity: '0.5',
    fontWeight: '500',
    fontSize: '0.7rem',
    margin: 0,
  },
  sendToFix: {
    display: 'flex',
    alignItems: 'center',
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
  },
}))

interface OrderMobileCardProps {
  row: OrderRowData
  isLast: boolean
  setOrderCheck: (orderId: number, valueDiary: boolean) => any
}

const OrderMobileCard = ({ row, isLast }: OrderMobileCardProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const orderDirectionValue = (orderDirection: string) => {
    if (orderDirection === 'OD_BUY') {
      return t('OD_BUY')
    } else {
      return t('OD_SELL')
    }
  }

  const orderValidityType = (validityType: string, validityDate: string) => {
    let result
    if (validityType === 'OV_GTD') {
      result = validityDate ? format(new Date(validityDate), 'dd/MM/yyyy') : ''
    } else {
      result = ''
    }
    return result
  }

  const marketTypeValue = (marketType: string) => {
    if (marketType === 'OMT_LIMIT') {
      return t('OMT_LIMIT')
    } else {
      return t('OMT_MARKET')
    }
  }

  const orderValidityValue = (orderValidity: string | number) => {
    if (orderValidity === 'OV_GTC') {
      return t('OV_GTC')
    } else {
      return t('OV_GTD')
    }
  }

  const getStateColor = (state: string) => {
    if (state === ORDER_STATE_NEW || state === ORDER_STATE_SENT_NEW) {
      return newThemeObject.palette.state.new
    } else if (state === ORDER_STATE_PARTIAL_FILLED) {
      return newThemeObject.palette.state.partiallyFilled
    } else if (state === ORDER_STATE_FILLED) {
      return newThemeObject.palette.state.filled
    } else if (state === ORDER_STATE_EXPIRED || state === ORDER_STATE_PARTIAL_EXPIRED) {
      return newThemeObject.palette.state.expired
    } else if (state === ORDER_STATE_REJECTED) {
      return newThemeObject.palette.state.rejected
    } else if (state === ORDER_STATE_CONFIRMED) {
      return newThemeObject.palette.state.confirmed
    } else if (state === ORDER_STATE_CANCELED || state === ORDER_STATE_PARTIAL_CANCELED) {
      return newThemeObject.palette.state.canceled
    } else if (state === ORDER_STATE_ERROR) {
      return newThemeObject.palette.state.error
    }
  }

  return (
    <div className={`${classes.card} ${row.direction === 'OD_SELL' ? classes.rowColorSell : ''}`}>
      <div className={classes.cardHeader}>
        <InstrumentIcon securityType={row.assetClass} />
        <div className={classes.cardHeaderText}>
          <p className={classes.cardName}>
            {row.companyName || `${row.surname} ${row.firstName}`}
          </p>
          <p className={classes.cardIsin}>
            {row.security} {row.isin || row.symbol ? `(${row.isin}${row.symbol ? `/${row.symbol}` : ''})` : ''}
          </p>
        </div>
        <p className={classes.cardOrderNumber}>{row.orderIdent}</p>
      </div>
      <div className={classes.cardGrid}>
        <div className={classes.cardField}>
          <p className={classes.cardLabel}>{t('CRETATION_DATE')}</p>
          <p className={classes.cardValue}>{format(new Date(row.orderCreationDate), 'dd/MM/yyyy')}</p>
        </div>
        <div className={classes.cardField}>
          <p className={classes.cardLabel}>{t('DIRECTION_ORDER_TYPE')}</p>
          <p className={classes.cardValue}>
            {orderDirectionValue(row.direction)} / {marketTypeValue(row.orderMarketType)}
          </p>
        </div>
        <div className={classes.cardField}>
          <p className={classes.cardLabel}>{t('DATE_FROM')}</p>
          <p className={classes.cardValue}>
            {orderValidityValue(row.validityType)} {orderValidityType(row.validityType, row.validityDate)}
          </p>
        </div>
        <div className={classes.cardField}>
          <p className={classes.cardLabel}>{t('PRICE_UNIT')}</p>
          <p className={classes.cardValue}>
            {formatNumber(row.price)} / {formatNumber(row.quantity, { decimalPlaces: 0, suffix: `${t('UNIT')}` })}
          </p>
        </div>
        <div className={classes.cardField}>
          <p className={classes.cardLabel}>{t('AMOUNT_CURRENCY')}</p>
          <p className={classes.cardValue}>
            {formatNumber(row.amount)} {row.currency}
          </p>
        </div>
        <div className={classes.cardField}>
          <p className={classes.cardLabel}>{t('ORDER_STATE')}</p>
          <div style={{ backgroundColor: getStateColor(row.state) }} className={classes.rowOrderState}>
            <p className={classes.rowOrderStateText}>{t(row.state)}</p>
          </div>
        </div>
        <div className={classes.cardField}>
          <p className={classes.cardLabel}>{t('SEND_TO_FIX')}</p>
          <div className={classes.sendToFix}>{row.sendToFix ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}</div>
        </div>
      </div>
      <div className={classes.cardFooter}>
        <ColoredIconButton
          size="small"
          onClick={() =>
            dispatch(
              openDialog(ORDER_DETAIL_DIALOG_ID, {
                clientId: row.clientId,
                securityId: row.securityId,
                orderId: row.orderId,
              })
            )
          }
          icon={<EditIcon fontSize="small" />}
        />
      </div>
    </div>
  )
}

export default OrderMobileCard
